import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { LocationOptionsComponent } from '@locations';
import { AvsDateRangeIds, DatePickerComponent } from '@ui';

import { BaseWidgetDeviceConfig, StatsSupportedTypes, WidgetDeviceChart } from '../../../models';
import { DeviceDataPointSelectorComponent, DeviceSelectorComponent } from '../../selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DeviceDataPointSelectorComponent,
    DeviceSelectorComponent,
    LocationOptionsComponent,
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    MatIconModule,
    DatePickerComponent,
  ],
  selector: 'avs-data-point-options',
  templateUrl: './data-point-options.component.html',
})
export class DataPointOptionsComponent {
  changed = output<Partial<WidgetDeviceChart>>();
  config = input<BaseWidgetDeviceConfig<string>>();
  showFullLocation = input<boolean>(true);
  showDataType = input<boolean>(false);
  hideDateRangeInput = input<boolean>(false);

  protected dataTypes = [
    {
      title: 'Power',
      value: StatsSupportedTypes.POWER,
    },
    {
      title: 'Temperature',
      value: StatsSupportedTypes.THERMOSTAT,
    },
  ];

  protected locationConfig = computed(() => {
    if (this.config()?.locationId) {
      return {
        floorId: this.config()!.floorId,
        locationId: this.config()!.locationId,
        roomId: this.config()!.roomId,
      };
    }

    return null;
  });

  protected eAvsDateRangeIds = AvsDateRangeIds;
  protected form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      datapointName: ['', Validators.required],
      dateRange: ['', Validators.required],
      deviceId: ['', Validators.required],
      locationId: ['', Validators.required],
    });

    const initialEffect = effect(() => {
      if (this.showDataType()) {
        this.form.addControl('dataType', new FormControl(('' as StatsSupportedTypes), [Validators.required]));
      }
      if (this.showFullLocation()) {
        this.form.addControl('floorId', new FormControl((''), [Validators.required]));
        this.form.addControl('roomId', new FormControl((''), [Validators.required]));
      }

      this.form.patchValue(this.config() as { [key: string]: any; });
      initialEffect.destroy();
    }, { manualCleanup: true });
  }

  protected patchForm(value: Record<string, undefined | AvsDateRangeIds | string>) {
    this.form.patchValue(value);
    this.changed.emit(this.form.valid ? this.form.value : null);
  }

  protected patchLocationForm(form: Partial<WidgetDeviceChart> | null) {
    this.form.reset();
    this.patchForm(form ?? {});
  }

  protected dataTypeChanged($event: string) {
    this.patchForm({ dataType: $event, deviceId: '' });
  }
}
